import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Clock, MessageSquare } from "lucide-react";
import { SupportChat } from "@/components/SupportChat";
import { useAuth } from "@/context/AuthContext";
import { Link, Navigate } from "react-router-dom";

export default function PendingApproval() {
  const { user, logout } = useAuth();
  const [supportOpen, setSupportOpen] = useState(false);
  if (!user) return null;

  // Account First, Application Second: pending shops now use the dashboard.
  // Only rejected and suspended shops see this page.
  if (user.status === "pending") {
    return <Navigate to="/shop" replace />;
  }

  const rejected = user.status === "rejected";
  const suspended = user.status === "suspended" || user.status === "deactivated";
  const hasPlan = user.subscription_tier && user.subscription_tier !== "free";

  let title = "Account issue";
  let intro = "There is an issue with your account.";
  let accent = "bg-[#FF3B30] text-white";
  let badge = "ISSUE";

  if (rejected) {
    title = "Application not approved";
    intro = "We're unable to approve your shop application at this time. You can fix the issue below and reapply, or contact support.";
    accent = "bg-[#FF3B30] text-white";
    badge = "REJECTED";
  } else if (suspended) {
    title = "Account suspended";
    intro = "Your shop account is currently suspended. While suspended you can't see jobs, send quotes, chat, take bookings or receive job alerts, and your shop is hidden from car owners.";
    accent = "bg-[#FF3B30] text-white";
    badge = "SUSPENDED";
  }

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <main className="max-w-[700px] mx-auto px-4 sm:px-8 lg:px-12 py-16" data-testid="pending-approval-page">
        <div className="border border-white/10 bg-[#121214] p-8 sm:p-12">
          <div className="flex items-center gap-3 mb-6">
            <span className={`text-xs uppercase tracking-widest px-2 py-1 ${accent}`} data-testid="status-badge">{badge}</span>
            <span className="label-eyebrow text-zinc-500">Shop account</span>
          </div>

          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-[#FF3B30] flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">{title}</h1>
              <p className="text-zinc-400 leading-relaxed">{intro}</p>
            </div>
          </div>

          {(rejected || suspended) && (
            <div className="border border-[#FFB800]/30 bg-[#FFB800]/5 p-4 mb-6" data-testid="status-reason">
              <div className="label-eyebrow text-[#FFB800] mb-2">{suspended ? "SUSPENSION REASON" : "REASON"}</div>
              <p className="text-sm text-zinc-300">
                {(suspended ? user.suspension_reason : user.rejection_reason) || "No reason was recorded. Contact support and we'll explain."}
              </p>
            </div>
          )}

          {rejected && (
            <Link to="/shop/apply" className="btn-primary inline-flex mb-6" data-testid="reapply-link">Update and reapply</Link>
          )}

          <div className="text-xs text-zinc-500 leading-relaxed space-y-1">
            <p>You can still: contact support, manage or cancel your subscription, and delete your account from Settings.</p>
            {hasPlan && (
              <p>Subscribed through the App Store or Google Play? Cancel it in your phone's store subscriptions; web plans can be managed below.</p>
            )}
          </div>

          <div className="border-t border-white/10 pt-6 mt-8">
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/settings" className="btn-secondary" data-testid="pending-settings-link">Account settings</Link>
              <Link to="/settings?tab=subscription" className="btn-secondary" data-testid="pending-billing-link">Manage / cancel subscription</Link>
              <Link to="/settings?tab=privacy" className="btn-secondary" data-testid="pending-delete-link">Delete account</Link>
              <Link to="/support" className="btn-secondary" data-testid="pending-support-link">Contact support</Link>
              <button onClick={logout} className="btn-secondary" data-testid="pending-logout-btn">Sign out</button>
            </div>
          </div>
        </div>
      </main>
      <button
        onClick={() => setSupportOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-full p-4 shadow-lg shadow-red-500/20 transition-all hover:scale-105"
        title="Need help?"
      >
        <MessageSquare className="w-5 h-5" />
      </button>
      <SupportChat open={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
}
